/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Thread preview card — Figma 7038:?? "Thread" (7831:9622). A single-row
// pill shown under a message bubble / stacked row:
//   - bg #222429 (surface.elevated), radius 12, pad L/R12 T/B8, gap 8
//   - reply/thread glyph (lucide MessageSquare 16 #F5F6F8)
//   - "${count} Replies" (Lato 700 14/19.6, #F5F6F8)
//   - optional 18×18 (radius 6) last-replier avatar + name (Lato 700 12,
//     #F5F6F8) + preview snippet (Lato 400 12, #B5BAC3, ellipsized)
//   - persistent trailing ChevronRight (#8C929E).

import React from "react";
import { Image, Pressable, Text, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { ChevronRight, MessageSquare } from "lucide-react-native";
import { createTheme } from "../design-system/theme";

export interface ThreadPreviewCardParticipant {
  readonly initials: string;
  readonly imageUri?: string | undefined;
}

export interface ThreadPreviewCardProps {
  readonly count: number;
  /** Peer replies newer than the local (DeviceGroup-synced) read pointer.
   *  Renders a brand unread pill when > 0. */
  readonly unreadCount?: number | undefined;
  readonly participants?:
    | ReadonlyArray<ThreadPreviewCardParticipant>
    | undefined;
  readonly lastReplierName?: string | undefined;
  readonly preview?: string | undefined;
  readonly onPress: () => void;
}

export const ThreadPreviewCard = React.memo<ThreadPreviewCardProps>(
  ({ count, unreadCount, participants, lastReplierName, preview, onPress }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === "dark" ? "dark" : "light");

    // First participant (most-recent replier) shows in the inline preview.
    const avatar = (participants ?? [])[0];
    const hasPreview =
      avatar !== undefined ||
      (lastReplierName !== undefined && lastReplierName.length > 0) ||
      (preview !== undefined && preview.length > 0);

    const unread = unreadCount ?? 0;
    const replyLabel = count === 1 ? "1 Reply" : `${count} Replies`;

    return (
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={
          unread > 0
            ? `View thread, ${replyLabel}, ${unread} unread`
            : `View thread, ${replyLabel}`
        }
        onPress={onPress}
        // Figma 7831:9622 thread chip — single row, #222429 / r12, 12·8 pad.
        className="flex-row items-center self-start rounded-[12px] border border-light-surface-tertiary dark:border-transparent bg-light-surface-secondary dark:bg-[#222429] px-3 py-2 gap-2 active:opacity-80"
      >
        <MessageSquare
          size={16}
          strokeWidth={2.25}
          color={scheme === "dark" ? "#F5F6F8" : theme.colors.text.primary}
        />
        {unread > 0 ? (
          <View className="min-w-[18px] h-[18px] px-1.5 rounded-full items-center justify-center bg-light-accent-brand dark:bg-[#537EFF]">
            <Text className="font-sans text-[11px] leading-[14px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </Text>
          </View>
        ) : null}
        <Text
          className="font-sans text-[14px] font-bold leading-[19.6px] text-light-text-primary dark:text-[#F5F6F8]"
        >
          {replyLabel}
        </Text>

        {hasPreview ? (
          <View className="flex-row items-center gap-1.5 flex-shrink min-w-0">
            {avatar !== undefined ? (
              <View className="w-[18px] h-[18px] rounded-[6px] overflow-hidden items-center justify-center bg-light-surface-tertiary dark:bg-[#2F333B]">
                {avatar.imageUri !== undefined ? (
                  <Image
                    source={{ uri: avatar.imageUri }}
                    accessibilityIgnoresInvertColors
                    resizeMode="cover"
                    className="w-full h-full"
                  />
                ) : (
                  <Text
                    style={{ color: theme.colors.text.secondary }}
                    className="font-sans text-[9px] font-bold leading-3"
                  >
                    {avatar.initials.slice(0, 2)}
                  </Text>
                )}
              </View>
            ) : null}
            {lastReplierName !== undefined && lastReplierName.length > 0 ? (
              <Text
                numberOfLines={1}
                className="font-sans text-[12px] font-bold leading-4 flex-shrink-0 text-light-text-primary dark:text-[#F5F6F8]"
              >
                {lastReplierName}
              </Text>
            ) : null}
            {preview !== undefined && preview.length > 0 ? (
              <Text
                numberOfLines={1}
                className="font-sans text-[12px] leading-4 flex-shrink min-w-0 text-light-text-secondary dark:text-[#B5BAC3]"
              >
                {preview}
              </Text>
            ) : null}
          </View>
        ) : null}

        <ChevronRight size={18} strokeWidth={2} color="#8C929E" />
      </Pressable>
    );
  },
);

ThreadPreviewCard.displayName = "ThreadPreviewCard";
